import React, { useState, useEffect } from "react";
import { agrogestao } from "@/api/agrogestaoClient";
import { useQuery } from "@tanstack/react-query";
import { format, subMonths, startOfMonth, endOfMonth, parseISO, isBefore, differenceInDays } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Wallet, TrendingUp, TrendingDown, Wheat, Loader2, Landmark, Warehouse, Droplet } from "lucide-react";
import StatsCard from "../components/dashboard/StatsCard";
import RecentTransactions from "../components/dashboard/RecentTransactions";
import AlertsPanel from "../components/dashboard/AlertsPanel";
import QuickActions from "../components/dashboard/QuickActions";
import FinanceChart from "../components/dashboard/FinanceChart";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function Dashboard() {
  const propriedadeId = localStorage.getItem("propriedade_selecionada");


  const { data: dashboardStats, isLoading: loadingStats } = useQuery({
    queryKey: ['dashboard_stats', propriedadeId],
    queryFn: async () => {
      if (!propriedadeId) return null;
      const { data, error } = await agrogestao.client.rpc('get_dashboard_stats', { p_propriedade_id: propriedadeId });
      if (error) throw error;
      return data;
    },
    enabled: !!propriedadeId
  });

  const { data: lancamentos = [], isLoading: loadingLancamentos } = useQuery({
    queryKey: ['lancamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Lancamento.filter({ propriedade_id: propriedadeId }, '-data', 10) // Limit reduced to 10 for "Recent Transactions" only
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: insumos = [] } = useQuery({
    queryKey: ['insumos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Insumo.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: safras = [] } = useQuery({
    queryKey: ['safras', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Safra.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: propriedade } = useQuery({
    queryKey: ['propriedade', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Propriedade.filter({ id: propriedadeId }).then(r => r[0])
      : Promise.resolve(null),
    enabled: !!propriedadeId
  });

  const { data: financiamentos = [] } = useQuery({
    queryKey: ['financiamentos', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.Financiamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: parcelasFinanciamento = [] } = useQuery({
    queryKey: ['parcelas_financiamento', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ParcelaFinanciamento.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasPagar = [] } = useQuery({
    queryKey: ['contas_pagar', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaPagar.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: contasReceber = [] } = useQuery({
    queryKey: ['contas_receber', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ContaReceber.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: estoquesGrao = [] } = useQuery({
    queryKey: ['estoques_grao', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.EstoqueBalanco.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: manejos = [] } = useQuery({
    queryKey: ['manejos_animais', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ManejoAnimal.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: producaoLeite = [] } = useQuery({
    queryKey: ['producao_leite', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.ProducaoLeite.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: tanquesLeite = [] } = useQuery({
    queryKey: ['tanques_leite', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.TanqueLeite.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const { data: movTanque = [] } = useQuery({
    queryKey: ['movimentacoes_tanque_leite', propriedadeId],
    queryFn: () => propriedadeId
      ? agrogestao.entities.MovimentacaoTanqueLeite.filter({ propriedade_id: propriedadeId })
      : Promise.resolve([]),
    enabled: !!propriedadeId
  });

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  // Calculations from RPC
  const totalReceitas = dashboardStats?.total_receitas || 0;
  const totalDespesas = dashboardStats?.total_despesas || 0;
  const saldo = dashboardStats?.saldo_total || 0;
  const saldoDevedor = dashboardStats?.saldo_devedor || 0;

  const safrasAtivas = safras.filter(s => s.status === 'em_andamento');
  const insumosBaixos = insumos.filter(i => i.quantidade_estoque <= (i.quantidade_minima || 0));
  const contasAtrasadas = [
    ...contasPagar.filter(c => c.status === 'atrasado'),
    ...contasReceber.filter(c => c.status === 'atrasado')
  ];

  // Financiamentos List (for alerts only)
  const financiamentosAtivos = financiamentos.filter(f => f.status === 'ativo');


  const hoje = new Date();
  const parcelasProximas = parcelasFinanciamento.filter(p => {
    if (p.status === 'pago') return false;
    const venc = parseISO(p.data_vencimento);
    const dias = differenceInDays(venc, hoje);
    return dias >= 0 && dias <= 30;
  });

  const manejosProximos = manejos.filter(m => {
    if (!m.proxima_data) return false;
    const prox = parseISO(m.proxima_data);
    const dias = differenceInDays(prox, hoje);
    return dias >= 0 && dias <= 30;
  });

  // Qualidade do leite (últimos 30 dias)
  const leiteUlt30 = producaoLeite.filter(p => {
    if (!p?.data) return false;
    const d = parseISO(p.data);
    const dias = differenceInDays(d, hoje);
    return dias >= 0 && dias <= 30;
  });
  const nums = (arr) => arr.filter(v => typeof v === 'number');
  const avg = (arr) => arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null;
  const avgGordura = avg(nums(leiteUlt30.map(p => p.gordura_pct)));
  const avgProteina = avg(nums(leiteUlt30.map(p => p.proteina_pct)));
  const acimaCCS = leiteUlt30.filter(p => typeof p.ccs === 'number' && p.ccs > 500000).length;
  const acimaCBT = leiteUlt30.filter(p => typeof p.cbt === 'number' && p.cbt > 300000).length;
  const qualidadeAlertas = (acimaCCS + acimaCBT) > 0 ? { acimaCCS, acimaCBT } : null;

  // Vendas de leite agregadas (últimos 30 dias)
  const saidasLeite30 = movTanque.filter(m => {
    if (!m?.data) return false;
    if (m.tipo !== 'saida') return false;
    const d = parseISO(m.data);
    const dias = differenceInDays(d, hoje);
    return dias >= 0 && dias <= 30;
  });
  const porTanque = (() => {
    const map = new Map();
    saidasLeite30.forEach(m => {
      const key = m.tanque_id;
      const cur = map.get(key) || { volume: 0, somaPrecoVolume: 0, receita: 0 };
      const vol = m.volume_litros || 0;
      const preco = m.preco_litro || 0;
      const val = (m.valor_total != null) ? m.valor_total : (preco * vol);
      cur.volume += vol;
      cur.somaPrecoVolume += preco * vol;
      cur.receita += val;
      map.set(key, cur);
    });
    return Array.from(map.entries()).map(([tanque_id, v]) => {
      const nome = tanquesLeite.find(t => t.id === tanque_id)?.nome || 'Tanque';
      const avgPreco = v.volume ? v.somaPrecoVolume / v.volume : 0;
      return { tanque_id, nome, volume: v.volume, avgPreco, receita: v.receita };
    }).sort((a, b) => b.receita - a.receita).slice(0, 5);
  })();
  const porCliente = (() => {
    const map = new Map();
    saidasLeite30.forEach(m => {
      const key = m.cliente || '—';
      const cur = map.get(key) || { receita: 0 };
      const vol = m.volume_litros || 0;
      const preco = m.preco_litro || 0;
      const val = (m.valor_total != null) ? m.valor_total : (preco * vol);
      cur.receita += val;
      map.set(key, cur);
    });
    return Array.from(map.entries()).map(([cliente, v]) => ({ cliente, receita: v.receita }))
      .sort((a, b) => b.receita - a.receita).slice(0, 5);
  })();

  const precoMedioPorCliente = (() => {
    const map = new Map();
    saidasLeite30.forEach(m => {
      const key = m.cliente || '—';
      const cur = map.get(key) || { vol: 0, receita: 0 };
      const vol = m.volume_litros || 0;
      const val = (m.valor_total != null) ? m.valor_total : ((m.preco_litro || 0) * vol);
      cur.vol += vol;
      cur.receita += val;
      map.set(key, cur);
    });
    return Array.from(map.entries()).map(([cliente, v]) => ({ cliente, avgPreco: v.vol ? v.receita / v.vol : 0 }))
      .sort((a, b) => b.avgPreco - a.avgPreco).slice(0, 5);
  })();

  // Estoque de grãos (usando a View consolidada)
  const totalEstoqueGraos = estoquesGrao.reduce((acc, e) => acc + (Number(e.quantidade_real) || 0), 0) / 60;
  // O valor total aqui é projetado. Idealmente buscaríamos o preço atual do produto
  const valorEstoqueGraos = totalEstoqueGraos * 120; // Valor base estimado (R$ 120/saca) se o custo médio não estiver na view

  // Chart data - last 6 months
  const getChartData = () => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const date = subMonths(new Date(), i);
      const monthStart = startOfMonth(date);
      const monthEnd = endOfMonth(date);

      const monthReceitas = lancamentos
        .filter(l => {
          const lDate = new Date(l.data);
          return l.tipo === 'receita' && (l.status === 'pago' || !l.status) && lDate >= monthStart && lDate <= monthEnd;
        })
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      const monthDespesas = lancamentos
        .filter(l => {
          const lDate = new Date(l.data);
          return l.tipo === 'despesa' && (l.status === 'pago' || !l.status) && lDate >= monthStart && lDate <= monthEnd;
        })
        .reduce((acc, l) => acc + (l.valor || 0), 0);

      months.push({
        mes: format(date, 'MMM', { locale: ptBR }),
        receitas: monthReceitas,
        despesas: monthDespesas
      });
    }
    return months;
  };

  if (!propriedadeId) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center">
        <div className="text-center max-w-md">
          <div className="w-20 h-20 bg-[#40916C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Wheat className="w-10 h-10 text-[#40916C]" />
          </div>
          <h2 className="text-2xl font-bold text-stone-800 mb-2">Bem-vindo ao AgroGestão!</h2>
          <p className="text-stone-600 mb-6">
            Cadastre sua primeira propriedade para começar a gerenciar suas finanças rurais.
          </p>
          <a
            href={`/Propriedades`}
            className="inline-flex items-center gap-2 bg-[#1B4332] text-white px-6 py-3 rounded-xl font-medium hover:bg-[#2D6A4F] transition-colors"
          >
            Cadastrar Propriedade
          </a>
        </div>
      </div>
    );
  }

  if (loadingLancamentos) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#40916C] animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-stone-800">
            Olá, Produtor! 👋
          </h1>
          <p className="text-stone-600 mt-1">
            {propriedade?.nome} • {format(new Date(), "EEEE, dd 'de' MMMM", { locale: ptBR })}
          </p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatsCard
          title="Saldo Total"
          value={formatCurrency(saldo)}
          subtitle="Receitas - Despesas"
          icon={Wallet}
          color={saldo >= 0 ? "green" : "red"}
        />
        <StatsCard
          title="Saldo Devedor"
          value={formatCurrency(saldoDevedor)}
          subtitle={`${financiamentosAtivos.length} financiamentos ativos`}
          icon={Landmark}
          color="red"
        />
        <StatsCard
          title="Estoque de Grãos"
          value={`${totalEstoqueGraos.toLocaleString('pt-BR')} sc`}
          subtitle={formatCurrency(valorEstoqueGraos)}
          icon={Warehouse}
          color="gold"
        />
        <StatsCard
          title="Safras Ativas"
          value={safrasAtivas.length}
          subtitle={`${safras.length} safras no total`}
          icon={Wheat}
          color="stone"
        />
      </div>

      {/* Quick Actions */}
      <QuickActions />

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Chart */}
        <div className="lg:col-span-2">
          <FinanceChart data={getChartData()} />
        </div>

        {/* Alerts */}
        <div>
          <AlertsPanel
            insumosBaixos={insumosBaixos}
            contasAtrasadas={contasAtrasadas}
            safrasAtivas={safrasAtivas}
            parcelasProximas={parcelasProximas}
            financiamentos={financiamentos}
            manejosProximos={manejosProximos}
            qualidadeAlertas={qualidadeAlertas}
          />
        </div>
      </div>

      {/* Milk Quality KPIs and Sales */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Droplet className="w-5 h-5 text-[#2D6A4F]" /> Leite — Qualidade (30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="p-3 bg-stone-50 rounded">
                <p className="text-stone-500 text-xs">Gordura média (%)</p>
                <p className="font-semibold text-stone-800">{avgGordura?.toFixed(2) ?? '-'}</p>
              </div>
              <div className="p-3 bg-stone-50 rounded">
                <p className="text-stone-500 text-xs">Proteína média (%)</p>
                <p className="font-semibold text-stone-800">{avgProteina?.toFixed(2) ?? '-'}</p>
              </div>
            </div>
            <div className="mt-3 text-sm text-stone-600">
              CCS acima: {acimaCCS} • CBT acima: {acimaCBT}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>Leite — Vendas (30 dias)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div>
                <p className="text-stone-600 text-sm mb-1">Por tanque</p>
                {porTanque.length === 0 ? (
                  <p className="text-stone-500 text-sm">Sem vendas</p>
                ) : (
                  porTanque.map(t => (
                    <div key={t.tanque_id} className="grid grid-cols-6 items-center gap-2 text-sm">
                      <div className="col-span-2 text-stone-700">{t.nome}</div>
                      <div className="col-span-2 text-right text-stone-700">{(t.volume || 0).toFixed(2)} L</div>
                      <div className="col-span-2 text-right text-stone-700">R$ {t.avgPreco?.toFixed(2)} / L</div>
                    </div>
                  ))
                )}
              </div>
              <div className="pt-2">
                <p className="text-stone-600 text-sm mb-1">Receita por cliente</p>
                {porCliente.length === 0 ? (
                  <p className="text-stone-500 text-sm">Sem vendas</p>
                ) : (
                  porCliente.map(c => (
                    <div key={c.cliente} className="grid grid-cols-6 items-center gap-2 text-sm">
                      <div className="col-span-4 text-stone-700">{c.cliente}</div>
                      <div className="col-span-2 text-right text-stone-700">{formatCurrency(c.receita)}</div>
                    </div>
                  ))
                )}
              </div>
              <div className="pt-2">
                <p className="text-stone-600 text-sm mb-1">Preço médio por cliente</p>
                {precoMedioPorCliente.length === 0 ? (
                  <p className="text-stone-500 text-sm">Sem vendas</p>
                ) : (
                  precoMedioPorCliente.map(c => (
                    <div key={c.cliente} className="grid grid-cols-6 items-center gap-2 text-sm">
                      <div className="col-span-4 text-stone-700">{c.cliente}</div>
                      <div className="col-span-2 text-right text-stone-700">R$ {c.avgPreco?.toFixed(2)} / L</div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Transactions */}
      <RecentTransactions lancamentos={lancamentos} />
    </div>
  );
}
